import { useState } from "react"

import { TooltipView } from "@/domains/common/components/tooltip-view"
import { getGaugeColorByIndex } from "../../utils/gauge-color-utils"

import type { LastEpochVoteDTO } from "./types"

interface LastEpochVoteBarViewProps {
  lastEpochVotes: Array<LastEpochVoteDTO>
}

/**
 * Pure View component that displays last epoch votes as a horizontal bar.
 * Matches the exact styling of PowerBreakdownBarView.
 * Shows the percentage distribution of votes across different gauges,
 * sorted from largest to smallest (left to right).
 * Colors go from darkest (highest %) to lightest (lowest %).
 */
export const LastEpochVoteBarView = ({ lastEpochVotes }: LastEpochVoteBarViewProps) => {
  const [isHovered, setIsHovered] = useState(false)

  if (lastEpochVotes.length === 0) {
    return null
  }

  // Calculate total percentage
  const totalPercentage = lastEpochVotes.reduce((sum, vote) => sum + vote.votePercent, 0)

  // Sort votes by percentage, largest first (highest to lowest)
  const sortedVotes = [...lastEpochVotes].sort((a, b) => b.votePercent - a.votePercent)

  // Calculate cumulative positions for div segments
  let cumulativeX = 0
  const segments = sortedVotes.map((vote, index) => {
    const percentage = vote.votePercent
    const x = cumulativeX
    cumulativeX += percentage

    return {
      vote,
      percentage,
      x,
      width: percentage,
      index,
      // Assign colors from darkest (index 0, highest %) to lightest (index 9, lowest %)
      color: getGaugeColorByIndex(index),
    }
  })

  return (
    <div className="w-full">
      {/* Label and Percentage */}
      <div className="mb-2 flex items-center justify-between text-xs text-t200">
        <span className="font-medium uppercase">Last Epoch Vote</span>
        <span className="font-mono">{totalPercentage.toFixed(1)}%</span>
      </div>

      {/* Bar Container - matches PowerBreakdownBarView styling */}
      <div
        className="relative h-9 w-full overflow-visible rounded-xl border border-p400-light shadow-inner cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Colored segments with borders - each segment represents a gauge vote */}
        <div className="relative flex h-full w-full rounded-xl">
          {segments.map((segment, idx) => {
            const isLast = idx === segments.length - 1

            return (
              <div
                key={segment.vote.gaugeAddress.toString()}
                className="h-full transition-opacity"
                style={{
                  width: `${segment.percentage}%`,
                  borderRight: isLast ? "none" : "1px solid hsl(var(--p400-light))",
                  opacity: isHovered ? 1 : 0.95,
                  backgroundColor: segment.color,
                  // Round left corners for first segment, right corners for last segment
                  borderTopLeftRadius: idx === 0 ? "0.75rem" : "0",
                  borderBottomLeftRadius: idx === 0 ? "0.75rem" : "0",
                  borderTopRightRadius: isLast ? "0.75rem" : "0",
                  borderBottomRightRadius: isLast ? "0.75rem" : "0",
                }}
              />
            )
          })}
        </div>

        {/* Single Tooltip showing all votes sorted by percentage */}
        {isHovered && (
          <TooltipView leftPercent={50}>
            <div className="flex flex-col gap-1.5 min-w-[200px]">
              <span className="mb-1 font-bold text-primary-400 uppercase text-[10px] tracking-wider">Previous Vote</span>
              {sortedVotes.map((vote, index) => (
                <div key={vote.gaugeAddress.toString()} className="flex items-center gap-2">
                  <div
                    className="h-2 w-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: getGaugeColorByIndex(index) }}
                  />
                  <span className="text-t100 flex-1 truncate">{vote.gaugeDisplayName}</span>
                  <span className="font-mono text-t200">{vote.votePercent.toFixed(0)}%</span>
                </div>
              ))}
            </div>
          </TooltipView>
        )}
      </div>
    </div>
  )
}
